import { useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Loader2, RefreshCw, ShieldOff, Send } from "lucide-react";
import { appsApi, errMsg, notifyError, fmtDate } from "@/lib/appsApi";

export const apiRoot = () => `${window.location.origin}/api/bundles`;

const copy = (text, what) => navigator.clipboard?.writeText(text).then(() => toast.success(`${what} copied`)).catch(() => notifyError("Copy failed: select the text and copy it yourself."));

const CopyLine = ({ label, value, testid }) => (
  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3" data-testid={testid}>
    <span className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{label}</span>
    <code className="min-w-0 flex-1 break-all rounded-xl bg-[#F4F9FD] px-3 py-2 text-xs text-ink">{value}</code>
    <button type="button" onClick={() => copy(value, label)} aria-label={`Copy ${label}`} className="self-start rounded-full p-2 text-slate-500 hover:bg-sky-50 hover:text-sky-700 sm:self-auto" data-testid={`${testid}-copy`}><Copy className="h-4 w-4" /></button>
  </div>
);

const Status = ({ unit }) => {
  if (unit.active) {
    return (
      <p className="text-xs text-slate-700" data-testid={`unit-status-${unit.id}`}>
        <span className="mr-2 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">Active</span>
        Token ending <span className="font-mono font-semibold text-ink">…{unit.token_last4}</span> · activated {fmtDate(unit.activated_at)}{unit.activated_device ? ` on ${unit.activated_device}` : ""}{unit.last_used_at ? ` · last used ${fmtDate(unit.last_used_at)}` : ""}
        {unit.code_pending && <span className="mt-1 block text-amber-800">A new activation code (…{unit.code_last4}) is waiting. This token stops working once it is used.</span>}
      </p>
    );
  }
  if (unit.code_pending) {
    return <p className="text-xs text-slate-700" data-testid={`unit-status-${unit.id}`}><span className="mr-2 rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-800">Waiting for activation</span>Code ending <span className="font-mono font-semibold text-ink">…{unit.code_last4}</span> · expires {fmtDate(unit.code_expires_at)}. Nothing works until the team's app enters it.</p>;
  }
  return <p className="text-xs text-slate-700" data-testid={`unit-status-${unit.id}`}><span className="mr-2 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">No access</span>Make an activation code and give it to the team.</p>;
};

const useTwoClick = () => {
  const [armed, setArmed] = useState("");
  return { armed, arm: (k) => setArmed(k), disarm: () => setArmed("") };
};

// Team access: one-time activation code -> token (held only by the team's app). Admins can re-issue, revoke and allow publishing to other teams.
export const UnitAccess = ({ appId, unit, onChanged }) => {
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState("");
  const confirm = useTwoClick();
  const code = fresh?.code || unit.code || "";
  const base = `${apiRoot()}/${appId}/${unit.id}`;

  const run = async (what, fn, msg) => {
    setBusy(what);
    try {
      const r = await fn();
      if (r?.data?.code) setFresh({ code: r.data.code, expires: r.data.code_expires_at });
      if (what === "revoke") setFresh(null);
      toast.success(msg);
      await onChanged();
    } catch (err) {
      notifyError(errMsg(err, "Updating team access"));
    } finally {
      setBusy("");
      confirm.disarm();
    }
  };
  const newCode = () => (unit.active && confirm.armed !== "code" ? confirm.arm("code")
    : run("code", () => appsApi.post(`/admin/bundles/${appId}/units/${unit.id}/code`), "Activation code made. Give it to the team."));
  const revoke = () => (confirm.armed !== "revoke" ? confirm.arm("revoke")
    : run("revoke", () => appsApi.post(`/admin/bundles/${appId}/units/${unit.id}/revoke`), `${unit.name} no longer has access`));
  const publish = () => run("publish", () => appsApi.patch(`/admin/bundles/${appId}/units/${unit.id}`, { can_publish: !unit.can_publish }),
    unit.can_publish ? `${unit.name} can now only publish to itself` : `${unit.name} can now publish to other teams`);

  return (
    <div className="mt-4 space-y-3 rounded-2xl border border-sky-100 p-4" data-testid={`unit-access-${unit.id}`}>
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink"><KeyRound className="h-4 w-4 text-sky-600" />App access for {unit.name}</p>
      <Status unit={unit} />
      {code && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid={`unit-code-new-${unit.id}`}>
          <p className="mb-2 text-xs font-medium text-amber-900">Give this one-time activation code to {unit.name}. It is shown only now, works once, and expires in 14 days.</p>
          <CopyLine label="Code" value={code} testid={`unit-code-value-${unit.id}`} />
        </div>
      )}
      <CopyLine label="Activate" value={`${apiRoot()}/activate`} testid={`unit-url-activate-${unit.id}`} />
      <CopyLine label="Team API" value={base} testid={`unit-url-base-${unit.id}`} />
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={newCode} disabled={!!busy} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors ${confirm.armed === "code" ? "bg-amber-600 text-white" : "border border-slate-200 text-ink hover:border-sky-600 hover:text-sky-700"}`} data-testid={`unit-code-make-${unit.id}`}>
          {busy === "code" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {confirm.armed === "code" ? "Current token stops once the new code is used. Confirm?" : unit.active || unit.code_pending ? "New activation code" : "Make activation code"}
        </button>
        {(unit.active || unit.code_pending) && (
          <button type="button" onClick={revoke} disabled={!!busy} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors ${confirm.armed === "revoke" ? "bg-red-600 text-white" : "text-slate-600 hover:bg-red-50 hover:text-red-600"}`} data-testid={`unit-revoke-${unit.id}`}>
            {busy === "revoke" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldOff className="h-3.5 w-3.5" />}{confirm.armed === "revoke" ? "Cut off access now?" : "Revoke access"}
          </button>
        )}
        <button type="button" onClick={publish} disabled={!!busy} aria-pressed={unit.can_publish} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors ${unit.can_publish ? "bg-sky-600 text-white" : "border border-slate-200 text-ink hover:border-sky-600"}`} data-testid={`unit-publish-toggle-${unit.id}`}>
          {busy === "publish" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}{unit.can_publish ? "Can publish to other teams" : "Publishes to itself only"}
        </button>
      </div>
    </div>
  );
};
